import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { BaseService } from 'src/app/shared/services/base.service';
import { WebStorageService } from 'src/app/shared/services/web-storage.service';
import { ResultOfT } from 'src/app/shared/models/response/result';
import {
  PatientSearchResult,
  CreatePatientRequest,
  CreateSalesOrderRequest,
  CreateSalesOrderResponse,
  TimeZoneLookupResult,
  CreateDoctorRequest,
  SavedDoctorResult,
  ClassificationDropdownItem,
  FacilityDropdownItem,
  InsuranceSearchResult,
  SaveInsuranceRequest,
  SalesOrderEditResponse
} from '../sales-order/models/sales-order-create.models';
import { BranchResponseModel } from 'src/app/shared/models/response/branch-response-model';
import { DoctorExclusion } from 'src/app/shared/models/response/doctor-response-model';
import { StateMasterResponseModel } from 'src/app/shared/models/common/state-master-response-model';

@Injectable({ providedIn: 'root' })
export class SalesOrderCreateService extends BaseService {

  // Cached observables — HTTP is called once per app session, replayed to all subscribers
  private doctors$: Observable<ResultOfT<DoctorExclusion[]>> | null = null;
  private branches$: Observable<ResultOfT<BranchResponseModel[]>> | null = null;
  private classifications$: Observable<ResultOfT<ClassificationDropdownItem[]>> | null = null;
  private facilities$: Observable<ResultOfT<FacilityDropdownItem[]>> | null = null;

  constructor(httpService: HttpClient, webStorageService: WebStorageService) {
    super(httpService, webStorageService);
  }

  searchPatients(searchText: string): Observable<ResultOfT<PatientSearchResult[]>> {
    const encoded = encodeURIComponent(searchText);
    return this.get<ResultOfT<PatientSearchResult[]>>(
      `SalesOrderManualCreate/SearchPatients?searchText=${encoded}&pageSize=20&pageIndex=0`
    );
  }

  getStates(): Observable<ResultOfT<StateMasterResponseModel[]>> {
    return this.get<ResultOfT<StateMasterResponseModel[]>>('Therapist/GetStates');
  }

  getPatient(crmPatientId: number): Observable<ResultOfT<PatientSearchResult>> {
    return this.get<ResultOfT<PatientSearchResult>>(`SalesOrderManualCreate/GetPatient/${crmPatientId}`);
  }

  checkRcmPatientIdExists(rcmPatientId: number): Observable<ResultOfT<boolean>> {
    return this.get<ResultOfT<boolean>>(`SalesOrderManualCreate/CheckRcmPatientIdExists/${rcmPatientId}`);
  }

  checkSalesOrderAppIdExists(appId: string): Observable<ResultOfT<boolean>> {
    return this.get<ResultOfT<boolean>>(`SalesOrderManualCreate/CheckSalesOrderAppIdExists?appId=${encodeURIComponent(appId)}`);
  }

  createPatient(request: CreatePatientRequest): Observable<ResultOfT<PatientSearchResult>> {
    return this.post<ResultOfT<PatientSearchResult>>('SalesOrderManualCreate/CreatePatient', request);
  }

  updatePatient(crmPatientId: number, request: CreatePatientRequest): Observable<ResultOfT<boolean>> {
    return this.post<ResultOfT<boolean>>(`SalesOrderManualCreate/UpdatePatient?crmPatientId=${crmPatientId}`, request);
  }

  createSalesOrder(request: CreateSalesOrderRequest): Observable<ResultOfT<CreateSalesOrderResponse>> {
    return this.post<ResultOfT<CreateSalesOrderResponse>>('SalesOrderManualCreate/CreateSalesOrder', request);
  }

  updateSalesOrder(salesOrderId: number, request: CreateSalesOrderRequest): Observable<ResultOfT<CreateSalesOrderResponse>> {
    return this.post<ResultOfT<CreateSalesOrderResponse>>(`SalesOrderManualCreate/UpdateSalesOrder/${salesOrderId}`, request);
  }

  /** Returns {label, value} shape. Result is cached for the lifetime of the service (app session). */
  getAllDoctors(): Observable<ResultOfT<DoctorExclusion[]>> {
    if (!this.doctors$) {
      this.doctors$ = this.get<ResultOfT<DoctorExclusion[]>>('Doctor/GetAllDoctors').pipe(shareReplay(1));
    }
    return this.doctors$;
  }

  /** Typeahead — search by partial name, returns ≤20 results. No caching (per-keystroke). */
  searchDoctors(query: string): Observable<ResultOfT<DoctorExclusion[]>> {
    const encoded = encodeURIComponent(query);
    return this.get<ResultOfT<DoctorExclusion[]>>(`Doctor/SearchDoctors?q=${encoded}`);
  }

  /** Fetch a single doctor by ID — used when pre-populating edit mode. */
  getDoctorById(doctorId: number): Observable<ResultOfT<{ crmDoctorId: number; crmDoctorFirstName?: string; crmDoctorLastName?: string }>> {
    return this.get(`Doctor/GetDoctor/${doctorId}`);
  }

  /** Get doctor display name by ID via SalesOrderManualCreate (Appointments permission only). */
  getDoctorName(doctorId: number): Observable<ResultOfT<string>> {
    return this.get<ResultOfT<string>>(`SalesOrderManualCreate/GetDoctorName/${doctorId}`);
  }

  /** Force a fresh fetch and update the cache (call after saving a new doctor if needed). */
  refreshDoctors(): void {
    this.doctors$ = this.get<ResultOfT<DoctorExclusion[]>>('Doctor/GetAllDoctors').pipe(shareReplay(1));
  }

  /** Save (create/update) a doctor — id=0 for new */
  saveDoctor(request: CreateDoctorRequest): Observable<ResultOfT<SavedDoctorResult>> {
    return this.post<ResultOfT<SavedDoctorResult>>('Doctor/Update', request);
  }

  /** Branch list is cached for the app session. */
  getBranchesList(): Observable<ResultOfT<BranchResponseModel[]>> {
    if (!this.branches$) {
      this.branches$ = this.get<ResultOfT<BranchResponseModel[]>>('Settings/GetBranchList').pipe(shareReplay(1));
    }
    return this.branches$;
  }

  /** Classification types — cached for the app session. */
  getClassificationsDropdown(): Observable<ResultOfT<ClassificationDropdownItem[]>> {
    if (!this.classifications$) {
      this.classifications$ = this.get<ResultOfT<ClassificationDropdownItem[]>>('SalesOrderManualCreate/GetClassificationsDropdown').pipe(shareReplay(1));
    }
    return this.classifications$;
  }

  /** Facilities — cached for the app session. */
  getFacilitiesDropdown(): Observable<ResultOfT<FacilityDropdownItem[]>> {
    if (!this.facilities$) {
      this.facilities$ = this.get<ResultOfT<FacilityDropdownItem[]>>('SalesOrderManualCreate/GetFacilitiesDropdown').pipe(shareReplay(1));
    }
    return this.facilities$;
  }

  getTimeZoneByZipCode(zipCode: string): Observable<ResultOfT<TimeZoneLookupResult>> {
    return this.get<ResultOfT<TimeZoneLookupResult>>(`SalesOrderManualCreate/GetTimeZoneByZipCode/${zipCode}`);
  }

  searchInsurance(q: string): Observable<ResultOfT<InsuranceSearchResult[]>> {
    return this.get<ResultOfT<InsuranceSearchResult[]>>(
      `SalesOrderManualCreate/SearchInsurance?q=${encodeURIComponent(q)}`
    );
  }

  saveInsurance(request: SaveInsuranceRequest): Observable<ResultOfT<boolean>> {
    return this.post<ResultOfT<boolean>>('SalesOrderManualCreate/SaveInsurance', request);
  }

  deleteInsurance(crmInsurancePatientId: number): Observable<ResultOfT<boolean>> {
    return this.delete<ResultOfT<boolean>>(`SalesOrderManualCreate/DeleteInsurance/${crmInsurancePatientId}`);
  }

  getSalesOrderForEdit(salesOrderId: number): Observable<ResultOfT<SalesOrderEditResponse>> {
    return this.get<ResultOfT<SalesOrderEditResponse>>(`SalesOrderManualCreate/GetSalesOrderForEdit/${salesOrderId}`);
  }
}
